import { BOX_CONDITIONS, SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { ISize } from 'shared/types/Size';
import { IVariant } from 'productTemplate/types/SizesMap';

/**
 * getSizesMap - takes in all the variants from /buy_bar_data and
 * returns a map with the size as the key and the value as an object
 * with the possible options: bestPrice, instantShip, newWithBoxIssues, newWithDefects, and used.
 * the values are the variant with the lowest price that fulfills certain conditions
 * Documentation: https://goatapp.atlassian.net/wiki/spaces/GOATcom/pages/2342977712/Template+SDD+document+-+Frontend+Edition#5.-Implementation%3A
 * @param buyBarData response from /buy_bar_data request Type: IVariant
 * @param sizeOptions: [{presentation: '3', value: 3}]
 * @returns {[key: number]: {bestPrice?: IVariant, instantShip: IVariant, newWithDefects: IVariant, newWithBoxIssues: IVariant, used: IVariant }}
 */
export const getSizesMap = (buyBarData: IVariant[], sizeOptions: ISize[]) => {
  if (!buyBarData || !sizeOptions) {
    return {};
  }

  const result = {};
  const validSizeSet = new Set(sizeOptions?.map((sizeOption) => sizeOption?.value));

  for (let i = 0; i < buyBarData?.length; i += 1) {
    const entry = buyBarData[i];
    const {
      stockStatus,
      shoeCondition,
      boxCondition,
      lowestPriceCents,
      instantShipLowestPriceCents,
      sizeOption: entrySizeOption,
    } = entry;
    const { value: size } = entrySizeOption;
    const validSize = validSizeSet.has(size);

    // Check that it is in stock, and is a valid size in sizeOption
    if (stockStatus !== 'not_in_stock' && validSize) {
      result[size] = result[size] || {};
      const sizeInfo = result[size];

      // Best Price
      if (
        shoeCondition === SHOE_CONDITIONS.NEW_NO_DEFECTS &&
        boxCondition === BOX_CONDITIONS.GOOD_CONDITION &&
        lowestPriceCents?.amount &&
        (!sizeInfo.bestPrice ||
          lowestPriceCents.amount < sizeInfo.bestPrice?.lowestPriceCents?.amount)
      ) {
        sizeInfo.bestPrice = entry;
      }

      // Instant Ship
      if (
        shoeCondition === SHOE_CONDITIONS.NEW_NO_DEFECTS &&
        instantShipLowestPriceCents?.amount &&
        (!sizeInfo.instantShip ||
          instantShipLowestPriceCents.amount <
            sizeInfo.instantShip?.instantShipLowestPriceCents?.amount)
      ) {
        sizeInfo.instantShip = entry;
      }

      // New with Box Issues
      if (
        shoeCondition === SHOE_CONDITIONS.NEW_NO_DEFECTS &&
        [
          BOX_CONDITIONS.NO_ORIGINAL_BOX,
          BOX_CONDITIONS.BADLY_DAMAGED,
          BOX_CONDITIONS.MISSING_LID,
        ].includes(boxCondition) &&
        lowestPriceCents?.amount &&
        (!sizeInfo.newWithBoxIssues ||
          lowestPriceCents.amount < sizeInfo.newWithBoxIssues?.lowestPriceCents?.amount)
      ) {
        sizeInfo.newWithBoxIssues = entry;
      }

      // New with Defects
      if (
        shoeCondition === SHOE_CONDITIONS.NEW_WITH_DEFECTS &&
        lowestPriceCents?.amount &&
        (!sizeInfo.newWithDefects ||
          lowestPriceCents.amount < sizeInfo.newWithDefects?.lowestPriceCents?.amount)
      ) {
        sizeInfo.newWithDefects = entry;
      }

      // Used
      if (
        [SHOE_CONDITIONS.USED, SHOE_CONDITIONS.GOAT_CLEAN].includes(shoeCondition) &&
        lowestPriceCents?.amount &&
        (!sizeInfo.used || lowestPriceCents.amount < sizeInfo.used?.lowestPriceCents?.amount)
      ) {
        sizeInfo.used = entry;
      }

      // Remove the size key if it's an empty object
      if (Object.keys(sizeInfo).length === 0) {
        delete result[size];
      }
    }
  }

  return result;
};
